
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faTrash, faCheckDouble } from '@fortawesome/free-solid-svg-icons';
import socket from '../../utils/socket';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import { API_URL } from '../../config';

import './Navbar.css';
import companyLogo from '../../LOGO/zasya-logo.jpg';

const API_BASE = API_URL.replace('/api', '');

const ZTaskLogo = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M2 17L12 22L22 17" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M2 12L12 17L22 12" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const Navbar = () => {
    const [notifications, setNotifications] = useState([]);
    const [isNotificationVisible, setNotificationVisible] = useState(false);
    const [hideTimeoutId, setHideTimeoutId] = useState(null);
    const navigate = useNavigate();

    const getUserDetails = () => {
        const userDetailsString = localStorage.getItem('zTaskUserDetails');
        if (userDetailsString) {
            try { return JSON.parse(userDetailsString); }
            catch (error) { console.error("Error parsing user details:", error); return null; }
        }
        return null;
    };

    const currentUser = getUserDetails();
    const userId = currentUser ? currentUser.id : null;
    const token = localStorage.getItem('zTaskAuthToken');

    // Fetch notifications initially
    useEffect(() => {
        if (!userId || !token) return;

        const fetchNotifications = async () => {
            try {
                const res = await axios.get(`${API_BASE}/api/notifications`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setNotifications(res.data || []);
            } catch (err) {
                console.error("Failed to fetch notifications:", err);
            }
        };

        fetchNotifications();
    }, [userId, token]);

    // WebSocket connection logic (Internal Socket.io)
    useEffect(() => {
        if (!userId) return;

        const handleNewNotification = (data) => {
            console.log('✅ Real-time Notification in Navbar:', data);

            // Add to list immediately
            setNotifications(prev => [data, ...prev]);

            // Note: Toast and Sound are handled by NotificationHandler.js globally to avoid duplication
        };

        socket.on('notification', handleNewNotification);

        return () => {
            socket.off('notification', handleNewNotification);
        };
    }, [userId]);


    const handleNotificationClick = (taskId) => {
        if (!taskId) {
            console.warn("Notification clicked, but no taskId was provided.");
            return;
        }

        navigate('/dashboard');

        setNotificationVisible(false);
    };

    // --- MODIFIED: Handles marking as seen ---
    const markAsSeen = async (notificationId, event) => {
        event.stopPropagation();
        try {
            await axios.put(`${API_BASE}/api/notifications/${notificationId}/read`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setNotifications(prev =>
                prev.map(notif => notif.id === notificationId ? { ...notif, is_read: true } : notif)
            );
        } catch (err) {
            console.error("Mark as read failed:", err);
        }
    };

    // --- MODIFIED: Handles clearing a single notification ---
    const clearNotification = async (notificationId, event) => {
        event.stopPropagation();
        try {
            await axios.delete(`${API_BASE}/api/notifications/${notificationId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setNotifications(prev => prev.filter(n => n.id !== notificationId));
        } catch (err) {
            console.error("Delete notification failed:", err);
        }
    };

    // --- MODIFIED: Handles clearing all notifications ---
    const markAllRead = async () => {
        try {
            await axios.put(`${API_BASE}/api/notifications/read-all`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
        } catch (err) {
            console.error("Mark all as read failed:", err);
        }
    };

    // Use is_read instead of seen to match backend column
    const unreadCount = notifications.filter(n => !n.is_read).length;

    const formatTimestamp = (ts) => {
        if (!ts) return '';
        return new Date(ts).toLocaleString('en-GB', {
            day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
        });
    };

    const handleMouseEnter = () => {
        if (hideTimeoutId) clearTimeout(hideTimeoutId);
        setNotificationVisible(true);
    };

    const handleMouseLeave = () => {
        const timeoutId = setTimeout(() => { setNotificationVisible(false); }, 300);
        setHideTimeoutId(timeoutId);
    };

    return (
        <>
            <ToastContainer />
            <nav className="navbar-container">
                <div className="navbar-left">
                    <Link to="/" className="navbar-logo-link">
                        <ZTaskLogo />
                        <span className="navbar-brand-name">ZHR</span>
                    </Link>
                </div>
                {userId && (
                    <div className="navbar-right">
                        <div
                            className="notification-wrapper"
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                        >
                            <button className="navbar-icon-button">
                                <FontAwesomeIcon icon={faBell} />
                                {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
                            </button>
                            {isNotificationVisible && (
                                <div className="notification-panel">
                                    <div className="notification-header">
                                        <h3>Notifications</h3>
                                        {unreadCount > 0 && (
                                            <button onClick={markAllRead} className="clear-all-btn">
                                                <FontAwesomeIcon icon={faCheckDouble} style={{ marginRight: '4px' }} /> Mark all read
                                            </button>
                                        )}
                                    </div>
                                    <div className="notification-list">
                                        {notifications.length > 0 ? (
                                            notifications.map((notif) => (
                                                <div
                                                    key={notif.id}
                                                    className={`notification-item ${notif.is_read ? 'notification-item--seen' : ''} notification-item--clickable`}
                                                    onClick={() => handleNotificationClick(notif.related_id)}
                                                >
                                                    <div className="notification-content">
                                                        <p>{notif.message}</p>
                                                        <span className="notification-timestamp">
                                                            {formatTimestamp(notif.created_at || notif.created_date)}
                                                        </span>
                                                    </div>
                                                    <div className="notification-actions">
                                                        {!notif.is_read && (
                                                            <button onClick={(e) => markAsSeen(notif.id, e)} className="mark-as-read-btn" title="Mark as read">
                                                                Read
                                                            </button>
                                                        )}
                                                        <button onClick={(e) => clearNotification(notif.id, e)} className="delete-notification-btn" title="Delete">
                                                            <FontAwesomeIcon icon={faTrash} />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="notification-item-empty"><p>No new notifications</p></div>
                                        )}
                                    </div>
                                </div>
                            )}

                        </div>
                        <a href="https://zasyaonline.com/" target="_blank" rel="noopener noreferrer" className="navbar-icon-button navbar-user-profile" aria-label="Company Website">
                            <img src={companyLogo} alt="Company Logo" className="navbar-company-logo" />
                        </a>
                    </div>
                )}
            </nav>
        </>
    );
};

export default Navbar;